<template>
  <div id="page" class="create-account">
    <frontoffice-loader v-if="isLoading" />

    <div v-else class="form-container">
      <b-container>
        <b-row>
          <b-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2" >
            <div class="contact-form">
              <b-row class="brand-section">
                <b-col cols="12" class="text-center logo">
                  <img src="@/assets/img/bba/logo.png" alt="bba_logo" />
                </b-col>

                <b-col cols="12" class="slogan text-center">
                  <h2>{{ $t('bba.slogan') }}</h2>
                </b-col>
              </b-row>

              <b-row v-if="displayIntroductionText">
                <p>{{ $t('create_account.intro.i1', { company: companyName }) }}</p>
                <p>{{ ($t('create_account.intro.i2')) }}</p>
                <ol>
                  <li v-for="i in 4" v-html="$t('create_account.intro.' + i)" />
                </ol>
              </b-row>

              <b-row class="justify-content-center">
                <div class="col-12 col-md-8 p-0">
                  <radio-input :label="labels.gender" name="gender"
                               :options="genderOptions" v-model="formData.gender"
                  />
                </div>

                <div class="col-12 col-md-8 p-0">
                  <text-input :label="labels.firstName" name="first-name"
                              v-model="formData.firstName"
                  />
                </div>

                <div class="col-12 col-md-8 p-0">
                  <text-input :label="labels.lastName" name="last-name"
                              v-model="formData.lastName"
                  />
                </div>

                <div class="col-12 col-md-8 p-0" v-if="displayCompany">
                  <text-input :label="labels.company" name="company"
                              v-model="formData.company"
                  />
                </div>

                <div class="col-12 col-md-8 p-0" v-if="displayJob">
                  <text-input :label="labels.job" name="job"
                              v-model="formData.job"
                  />
                </div>

                <div class="col-12 col-md-8 p-0">
                  <text-input :label="labels.mobilePhone" name="mobile-phone"
                              v-model="formData.mobilePhone" :required="false"
                              :placeholder="$t('create_account.phone_placeholder')"
                  />
                </div>

                <div class="col-12 col-md-8 p-0">
                  <text-input :label="labels.landline" name="landline"
                              v-model="formData.landline" :required="false"
                              :placeholder="$t('create_account.phone_placeholder')"
                  />
                </div>

                <div class="col-12 col-md-8 p-0">
                  <text-input :label="labels.email" name="email" type="email"
                              v-model="formData.email" @input="onEmailInput"
                  />
                </div>
              </b-row>

              <b-row v-if="isSubmitted" class="justify-content-center">
                <b-alert v-if="error" variant="danger" dismissible show
                         class="col-12 col-md-8"
                >
                  <ul>
                    <li v-for="error in errors" :key="error.id" class="error">
                      <span class="label">
                        {{ getErrorLabelTranslation(error.field) | capitalise }} :
                      </span>

                      {{ error.message }}
                    </li>
                  </ul>
                </b-alert>

                <b-alert v-if="success" variant="success" dismissible show
                         class="col-12 col-md-8"
                >
                  {{ successMessage }}
                </b-alert>
              </b-row>

              <b-row class="justify-content-center">
                <b-col v-if="goBackRouterLink" cols="4"
                       class="text-center button-link"
                >
                  <router-link class="orange-white btn" :to="goBackRouterLink">
                    {{ $t('_back') }}
                  </router-link>
                </b-col>

                <b-col cols="4" class="text-center button-link" v-if="!success">
                  <router-link class="orange-white btn" :to="newPassRouterLink"
                               v-if="error === existingEmailError && !emailChangedAfterError"
                  >
                    {{ $t('_define_password') }}
                  </router-link>

                  <button v-else class="orange-white btn" @click="formSubmit"
                          :disabled="status === 'submitting'"
                  >
                    {{ $t('_send') }}
                  </button>
                </b-col>
              </b-row>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </div>

    <vue-recaptcha
      ref="recaptcha"
      @verify="onCaptchaVerified"
      @expired="onCaptchaExpired"
      size="invisible"
      :sitekey="$store.state.security.captchaSiteKey"
    />
  </div>
</template>

<script>
import radioInput from '@/components/frontoffice/form/baseInput/radio'
import textInput from '@/components/frontoffice/form/baseInput/text'
import FormSubmitter from '@/api/formSubmitter'
import { errorCodes } from '@/utils/FormErrorCodes'
import CommonApi from '@/api/commonApi'
import frontofficeLoader from '@/components/frontoffice/loader'
import vueRecaptcha from 'vue-recaptcha'

export default {
  components: {
    frontofficeLoader,
    radioInput,
    textInput,
    vueRecaptcha
  },

  data () {
    return {
      isLoading: false,
      commonApi: new CommonApi(),
      companyUid: null,
      formSubmitter: new FormSubmitter('create_account'),
      genderOptions: [
        { value: 'male', label: '_male', selected: false },
        { value: 'female', label: '_female', selected: false }
      ],
      labels: {
        gender: '_gender',
        firstName: '_first_name',
        lastName: '_last_name',
        company: '_company',
        job: '_job',
        landline: '_landline',
        mobilePhone: '_mobile_phone',
        phones: '_phone_number',
        email: '_email'
      },
      formData: {
        gender: null,
        firstName: null,
        lastName: null,
        company: null,
        job: null,
        landline: null,
        mobilePhone: null,
        email: null,
        recaptchaToken: ""
      },
      isSubmitted: false,
      success: false,
      error: false,
      errors: [],
      emailChangedAfterError: false,
      companyName: null,
      status: ""
    }
  },

  created () {
    this.companyUid = this.$route.params.companyUid || null
    if (this.companyUid) {
      this.isLoading = true
      this.commonApi.getGenericCompanyData(this.companyUid)
        .then( response => {
          this.companyName = response.data.data.name
          this.isLoading = false
        })
    }
  },

  methods: {
    onEmailInput (email) {
      this.emailChangedAfterError = true
    },

    formSubmit () {
      this.status = "submitting";
      this.$refs.recaptcha.execute()
    },

    dealSuccess (data) {
      this.error = false
      this.success = true

      if (this.companyUid) {
        let bookings = this.$store.getters['generic/bookings']

        if (bookings.length)
          this.commonApi.bindUserToBookings(data.id, bookings)
            .then( response => {
              if (response.data.success) {
                this.$store.commit('generic/removeBookings')
              }
            })
      } else {
        setTimeout( () => {
          this.$router.push('login')
        }, 5000)
      }
    },

    dealFailure (response) {
      this.success = false
      this.errors = []

      for (let fields in response) {
        if (response.hasOwnProperty(fields)) {
          let code = parseInt(response[fields])
          this.error = code
          let message = this.$i18n.t('error.' + code)
          let listFields = []

          if (fields.indexOf(',') !== -1) {
            listFields = fields.split(',')
          } else {
            listFields = [fields]
          }

          for (let field of listFields) {
            this.errors.push({
              field: field,
              message: message
            })
          }
        }
      }
    },

    getErrorLabelTranslation (field) {
      return this.$i18n.t(this.labels[field])
    },

    onCaptchaVerified (recaptchaToken) {
      const self = this;
      self.formData.recaptchaToken = recaptchaToken
      self.status = "submitting";
      self.$refs.recaptcha.reset();

      this.formSubmitter.submitRequest(this.formData, this.companyUid)
        .then(response => {
          if (response.data.success) {
            this.dealSuccess(response.data.data)
          } else {
            this.dealFailure(response.data.errors)
          }

          this.emailChangedAfterError = false
          this.isSubmitted = true
        })
        .then(() => {
          self.status = ""
        })
    },

    onCaptchaExpired () {
      this.$refs.recaptcha.reset()
    }
  },

  computed: {
    goBackRouterLink () {
      if (this.companyUid) {
        // HACK BEFORE CLIENT VALIDATE FUNCTIONALITY IN V2
        return false

        if (this.isSubmitted && this.success) return false
        else return {
          name: 'home_generic_company',
          params: { companyUid: this.companyUid }
        }
      } else {
        return { name: 'login' }
      }
    },

    newPassRouterLink () {
      return {
        name: 'define_password',
        params: { uid: this.formData.email }
      }
    },

    successMessage () {
      return this.companyUid ? '' +
        this.$t('create_account.success.define_password', { email: this.formData.email })
        : this.$t('create_account.success.info')
    },

    existingEmailError () {
      return errorCodes['EXISTING_EMAIL']
    },

    displayCompany () {
      return this.companyUid === null
    },

    displayJob () {
      return this.companyUid === null
    },

    displayIntroductionText () {
      return this.companyUid !== null
    }
  }
}
</script>

<style lang="scss">
  @import '@/assets/sass/login-account.scss';
</style>
